import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Clock,
  MoreHorizontal,
  GripVertical,
} from 'lucide-react';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SequenceStep } from '@/lib/services/campaigns';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface StepCardProps {
  step: SequenceStep;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  sequence: SequenceStep[];
  setSequence: (sequence: SequenceStep[]) => void;
  getDelayOptions: () => Array<{ value: string; label: string }>;
  isAnyDragging: boolean;
  isNewlyAdded: boolean;
  isReadOnly?: boolean;
  hasError?: boolean;
  missingSubject?: boolean;
  missingContent?: boolean;
  missingSender?: boolean;
}

export function StepCard({
  step,
  index: _index,
  isSelected,
  onSelect,
  onDelete,
  onDuplicate,
  sequence,
  setSequence,
  getDelayOptions,
  isAnyDragging,
  isNewlyAdded,
  isReadOnly = false,
  hasError = false,
  missingSubject = false,
  missingContent = false,
  missingSender = false,
}: StepCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const [isDelaySelectOpen, setIsDelaySelectOpen] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: step.id,
  });

  return (
    <div 
      ref={setNodeRef} 
      style={{
        transform: isDragging ? undefined : CSS.Transform.toString(transform),
        transition: isDragging ? 'none' : (transition || undefined),
      }}
      className={`relative mb-6 group ${isDragging ? 'opacity-0 pointer-events-none' : ''}`}
      data-sortable-id={step.id}
      onMouseEnter={() => !isDragging && setIsHovered(true)}
      onMouseLeave={() => !isDragging && setIsHovered(false)}
    >
      {!isDragging && (
        <div 
          className="absolute left-0 top-0 bottom-0 -translate-x-12 w-12 z-0"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        />
      )}
      
      <Card
        {...(isDragging || isReadOnly ? {} : { ...attributes, ...listeners })}
        className={`${isReadOnly ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'} shadow-md rounded-inner pt-5 pb-5 border-1 border-border relative w-full max-w-[340px] mx-auto h-[140px] ${
          isSelected
            ? hasError
              ? 'bg-surface-subtle outline outline-3 outline-ring-error outline-offset-0'
              : 'bg-surface-subtle outline outline-3 outline-focus-ring outline-offset-0'
            : hasError
              ? 'outline outline-2 outline-ring-error-subtle outline-offset-0 hover:bg-card'
              : 'hover:bg-card'
        }`}
        onClick={isDragging ? undefined : onSelect}
        style={isNewlyAdded ? {
          animation: 'stepExpand 0.35s cubic-bezier(0.22, 1, 0.36, 1) forwards',
          transformOrigin: 'top center',
        } : undefined}
      >
        {false && isHovered && !isAnyDragging && (
          <div
            {...attributes}
            {...listeners}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-8 cursor-grab active:cursor-grabbing z-10"
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <div className="bg-surface-raised rounded-full p-1.5 shadow-sm flex items-center justify-center h-8 w-6">
              <GripVertical className="h-7 w-5 text-content-subtle" />
            </div>
          </div>
        )}
        
        <CardContent className={`px-4 pt-10 pb-4 relative`}
          style={isNewlyAdded ? {
            animation: 'fadeIn 0.2s ease-out 0.2s forwards',
            opacity: 0,
          } : undefined}
        >
          <div className="absolute top-0 left-4">
            {!isDelaySelectOpen ? (
              <Tooltip delayDuration={800}>
                <TooltipTrigger asChild>
                  <div>
                    <Select
                      value={(step.delay || 0).toString()}
                      open={isDelaySelectOpen}
                      onOpenChange={isReadOnly ? undefined : setIsDelaySelectOpen}
                      onValueChange={(isDragging || isReadOnly) ? undefined : (value) => {
                        const updatedSequence = sequence.map(s => 
                          s.id === step.id 
                            ? { ...s, delay: parseInt(value) }
                            : s
                        );
                        setSequence(updatedSequence);
                      }}
                      disabled={isDragging || isReadOnly}
                    >
                      <SelectTrigger 
                        className="w-auto min-w-[120px] h-8 text-xs bg-input-bg border border-control-border hover:bg-input-bg rounded-full pl-3 pr-2 [&>span]:mr-3"
                        onClick={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                      >
                        <Clock className="h-3.5 w-3.5 text-muted-foreground mr-2 flex-shrink-0" />
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Send immediately</SelectItem>
                        {getDelayOptions().map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" sideOffset={10}>
                  <p>{isReadOnly ? "Can't edit while campaign is live" : 'Set how long to wait before sending this email'}</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              <Select
                value={(step.delay || 0).toString()}
                open={isDelaySelectOpen}
                onOpenChange={isReadOnly ? undefined : setIsDelaySelectOpen}
                onValueChange={(isDragging || isReadOnly) ? undefined : (value) => {
                  const updatedSequence = sequence.map(s => 
                    s.id === step.id 
                      ? { ...s, delay: parseInt(value) }
                      : s
                  );
                  setSequence(updatedSequence);
                }}
                disabled={isDragging || isReadOnly}
              >
                <SelectTrigger 
                  className="w-auto min-w-[120px] h-8 text-xs bg-input-bg border border-control-border hover:bg-input-bg rounded-full pl-3 pr-2 [&>span]:mr-3"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <Clock className="h-3.5 w-3.5 text-muted-foreground mr-2 flex-shrink-0" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Send immediately</SelectItem>
                  {getDelayOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
          
          {!isReadOnly && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="secondary" 
                  size="xs" 
                  className="absolute top-0 right-4 !h-8 !w-8 !p-0 !px-0 !py-0 rounded-full aspect-square" 
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  disabled={isDragging}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={onDuplicate}>
                  Duplicate
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="text-danger-foreground"
                  onClick={onDelete}
                >
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          <div className="flex items-center gap-3 mt-3">
            <EnvelopeIcon className="h-6 w-6 text-foreground flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground mb-0">Email</p>
              {step.subject && step.subject.trim() !== '' && (
                <p className="text-xs truncate">
                  {step.subject}
                </p>
              )}
              {hasError && (missingSubject || missingContent || missingSender) && (
                <p className="text-xs text-danger-text mt-0.5">
                  {(() => {
                    const missing = [];
                    if (missingSender) missing.push('Sender');
                    if (missingSubject) missing.push('subject');
                    if (missingContent) missing.push('content');
                    
                    if (missing.length === 3) {
                      return 'Sender, subject and content required';
                    } else if (missing.length === 2) {
                      return `${missing[0]} and ${missing[1]} required`;
                    } else {
                      return `${missing[0]} required`;
                    }
                  })()}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Drag overlay version of the card (static, non-interactive)
interface StepCardOverlayProps {
  step: SequenceStep;
  wasSelected: boolean;
  getDelayOptions: () => Array<{ value: string; label: string }>;
  hasError?: boolean;
  missingSubject?: boolean;
  missingContent?: boolean;
  missingSender?: boolean;
}

export function StepCardOverlay({
  step,
  wasSelected,
  getDelayOptions,
  hasError = false,
  missingSubject = false,
  missingContent = false,
  missingSender = false,
}: StepCardOverlayProps) {
  return (
    <div className="w-full max-w-[340px] mx-auto">
      <Card className={`cursor-pointer rounded-inner pt-5 pb-5 shadow-md opacity-100 relative h-[140px] ${
        wasSelected
          ? hasError
            ? 'border-1 border-border outline outline-3 outline-ring-error outline-offset-0'
            : 'border-1 border-border outline outline-3 outline-focus-ring outline-offset-0'
          : hasError
            ? 'border-1 outline outline-2 outline-ring-error-subtle outline-offset-0'
            : 'border-1'
      }`}>
        {false && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-8 z-10">
            <div className="bg-surface-raised rounded-full p-1.5 shadow-sm flex items-center justify-center h-8 w-6">
              <GripVertical className="h-7 w-5 text-content-subtle" />
            </div>
          </div>
        )}
        <CardContent className="px-4 pt-10 pb-4 relative">
          <div className="absolute top-0 left-4">
            <Tooltip delayDuration={800}>
              <TooltipTrigger asChild>
                <div>
                  <Select
                    value={(step.delay || 0).toString()}
                    disabled
                  >
                    <SelectTrigger 
                      className="w-auto min-w-[120px] h-8 text-xs bg-input-bg border border-control-border hover:bg-input-bg rounded-full pl-3 pr-2 [&>span]:mr-3"
                    >
                      <Clock className="h-3.5 w-3.5 text-muted-foreground mr-2 flex-shrink-0" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Send immediately</SelectItem>
                      {getDelayOptions().map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" sideOffset={10}>
                <p>Set how long to wait before sending this email</p>
              </TooltipContent>
            </Tooltip>
          </div>
          
          <div className="absolute top-0 right-4">
            <Button 
              variant="secondary" 
              size="xs" 
              className="!h-8 !w-8 !p-0 !px-0 !py-0 shadow-2xl rounded-full aspect-square" 
              disabled
              style={{ opacity: 1 }}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="flex items-center gap-3 mt-3">
            <EnvelopeIcon className="h-6 w-6 text-foreground flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground mb-0">Email</p>
              {step.subject && step.subject.trim() !== '' && (
                <p className="text-xs truncate">{step.subject}</p>
              )}
              {hasError && (missingSubject || missingContent || missingSender) && (
                <p className="text-xs text-danger-text mt-0.5">
                  {(() => {
                    const missing = [];
                    if (missingSender) missing.push('Sender');
                    if (missingSubject) missing.push('subject');
                    if (missingContent) missing.push('content');

                    if (missing.length === 3) {
                      return 'Sender, subject and content required';
                    } else if (missing.length === 2) {
                      return `${missing[0]} and ${missing[1]} required`;
                    } else {
                      return `${missing[0]} required`;
                    }
                  })()}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

